import React, { useState, useMemo } from 'react';
import { useTasks } from '../../hooks/useTasks';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useUsers } from '../../hooks/useUsers';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import Modal from '../common/Modal';
import TaskForm from '../tasks/TaskForm';
import TaskView from '../tasks/TaskView';
import SearchableSelect from '../common/SearchableSelect';
import { 
  BriefcaseIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TagIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

interface ProjectTasksModalProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onOpenTaskForm?: (task?: Task) => void; // Callback a TaskForm megnyitására
}

const ProjectTasksModal: React.FC<ProjectTasksModalProps> = ({ project, isOpen, onClose }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { tasks, createTask, updateTask, deleteTask } = useTasks();
  const { users } = useUsers();

  // State management
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  
  // Inline form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    status: 'todo' as Task['status'],
    assignedTo: '',
    dueDate: '',
    estimatedHours: '',
    actualHours: '',
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Projekthez tartozó taskok szűrése
  const projectTasks = tasks.filter(task => task.projectId === project.id);

  // User options for SearchableSelect
  const userOptions = users.map(user => ({
    value: user.id,
    label: user.displayName ? `${user.displayName} (${user.email})` : user.email
  }));

  // Handle user selection
  const handleUserSelect = (userId: string) => {
    setFormData({...formData, assignedTo: userId});
  };

  // Task státusz színek
  const getTaskStatusColor = (status: Task['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (status) {
      case 'todo':
        return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-300';
      case 'in_progress':
        return isDark ? 'bg-blue-900/20 text-blue-300 border-blue-600' : 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed':
        return isDark ? 'bg-green-900/20 text-green-300 border-green-600' : 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled':
        return isDark ? 'bg-red-900/20 text-red-300 border-red-600' : 'bg-red-100 text-red-800 border-red-300';
      default:
        return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Task prioritás színek
  const getTaskPriorityColor = (priority: Task['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (priority) {
      case 'low':
        return isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800';
      case 'medium':
        return isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'high':
        return isDark ? 'bg-orange-900/20 text-orange-300' : 'bg-orange-100 text-orange-800';
      case 'urgent':
        return isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  // Prioritás ikonok
  const getPriorityIcon = (priority: Task['priority']) => {
    switch (priority) {
      case 'low':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'medium':
        return <ClockIcon className="h-4 w-4" />;
      case 'high':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'urgent':
        return <FireIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  // Státusz ikonok
  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'todo':
        return <ClockIcon className="h-4 w-4" />;
      case 'in_progress':
        return <ChartBarIcon className="h-4 w-4" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  // Task létrehozás - inline editing
  const handleCreateTask = () => {
    console.log('🆕 Opening inline TaskForm for creation');
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      assignedTo: '',
      dueDate: '',
      estimatedHours: '',
      actualHours: '',
      tags: []
    });
    setIsTaskFormOpen(true);
  };

  // Task szerkesztés - inline editing
  const handleEditTask = (task: Task) => {
    console.log('🔄 Opening inline TaskForm for editing');
    console.log('🔄 ProjectTasksModal - Task data:', task);
    console.log('🔄 ProjectTasksModal - assignedTo:', task.assignedTo);
    console.log('🔄 ProjectTasksModal - assignedToName:', task.assignedToName);
    console.log('🔄 ProjectTasksModal - Available users:', users);
    
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      priority: task.priority,
      status: task.status,
      assignedTo: task.assignedTo || '',
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 16) : '',
      estimatedHours: task.estimatedHours ? task.estimatedHours.toString() : '',
      actualHours: task.actualHours ? task.actualHours.toString() : '',
      tags: task.tags || []
    });
    setIsTaskFormOpen(true);
  };

  // Task megtekintés
  const handleViewTask = (task: Task) => {
    setViewingTask(task);
  };

  // Kommentek exportálása TXT fájlba
  const handleExportComments = () => {
    try {
      let content = `${project.name} - Feladat Kommentek\n`;
      content += `Exportálva: ${new Date().toLocaleString('hu-HU')}\n`;
      content += `${'='.repeat(80)}\n\n`;

      projectTasks.forEach((task, index) => {
        content += `${index + 1}. ${task.taskNumber || 'N/A'} - ${task.title}\n`;
        content += `${'-'.repeat(80)}\n`;
        
        if (task.comments && task.comments.length > 0) {
          task.comments.forEach((comment, commentIndex) => {
            const date = comment.createdAt instanceof Date 
              ? comment.createdAt.toLocaleString('hu-HU')
              : new Date(comment.createdAt).toLocaleString('hu-HU');
            
            content += `\n[${commentIndex + 1}] ${comment.userName} - ${date}\n`;
            content += `${comment.message}\n`;
          });
        } else {
          content += `\nNincs komment ehhez a feladathoz.\n`;
        }
        
        content += `\n${'='.repeat(80)}\n\n`;
      });

      // Fájl letöltése
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.name.replace(/[^a-z0-9]/gi, '_')}_kommentek_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Kommentek sikeresen exportálva!');
    } catch (error) {
      console.error('Error exporting comments:', error);
      toast.error('Hiba történt az exportálás során');
    }
  };

  // Task törlés
  const handleDeleteTask = async (task: Task) => {
    if (window.confirm(t('tasks.messages.confirmDelete') || 'Biztosan törölni szeretné ezt a feladatot?')) {
      try {
        await deleteTask(task.id);
        toast.success(t('tasks.messages.deleteSuccess') || 'Feladat sikeresen törölve!');
      } catch (error) {
        console.error('Error deleting task:', error);
        toast.error(t('tasks.messages.deleteError') || 'Hiba történt a feladat törlése során');
      }
    }
  };

  // Task mentés (új vagy szerkesztés)
  const handleSaveTask = async (taskData: any) => {
    try {
      if (editingTask) {
        // Update existing task
        await updateTask(editingTask.id, {
          ...taskData,
          projectId: project.id,
          customerId: project.customerId
        });
        toast.success(t('tasks.messages.updateSuccess') || 'Feladat sikeresen frissítve!');
      } else {
        // Create new task
        await createTask({
          ...taskData,
          projectId: project.id,
          customerId: project.customerId,
          organizationId: user?.organizationId || '',
          createdBy: user?.uid || '',
          createdByName: user?.displayName || user?.email || ''
        });
        toast.success(t('tasks.messages.createSuccess') || 'Feladat sikeresen létrehozva!');
      }
      setIsTaskFormOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      const errorMessage = editingTask 
        ? (t('tasks.messages.updateError') || 'Hiba történt a feladat frissítése során')
        : (t('tasks.messages.createError') || 'Hiba történt a feladat létrehozása során');
      toast.error(errorMessage);
    }
  };

  // Inline státusz frissítés
  const handleStatusChange = async (task: Task, newStatus: Task['status']) => {
    try {
      const updateData: any = {
        ...task,
        status: newStatus
      };
      
      // Only set completedDate if status is completed, otherwise remove it
      if (newStatus === 'completed') {
        updateData.completedDate = new Date();
      } else {
        updateData.completedDate = null;
      }
      
      // Clean up any undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          updateData[key] = null;
        }
      });
      
      await updateTask(task.id, updateData);
      toast.success(t('tasks.messages.statusUpdateSuccess') || 'Státusz sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('tasks.messages.statusUpdateError') || 'Hiba történt a státusz frissítése során');
    }
  };

  // Inline prioritás frissítés
  const handlePriorityChange = async (task: Task, newPriority: Task['priority']) => {
    try {
      const updateData: any = {
        ...task,
        priority: newPriority
      };
      
      // Clean up any undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          updateData[key] = null;
        }
      });
      
      await updateTask(task.id, updateData);
      toast.success(t('tasks.messages.priorityUpdateSuccess') || 'Prioritás sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating task priority:', error);
      toast.error(t('tasks.messages.priorityUpdateError') || 'Hiba történt a prioritás frissítése során');
    }
  };

  // Inline form mentés
  const handleInlineSave = async () => {
    if (!formData.title.trim()) {
      toast.error(t('tasks.validation.titleRequired') || 'Feladat címe kötelező!');
      return;
    }

    setIsSubmitting(true);
    console.log('💾 Saving inline form data:', formData);

    try {
      if (editingTask) {
        // Update existing task - prepare data object
        const updateData: any = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          priority: formData.priority,
          status: formData.status,
          tags: formData.tags
        };

        // Only add fields if they have values (avoid undefined)
        if (formData.assignedTo) {
          updateData.assignedTo = formData.assignedTo;
          // Find the user name for assignedToName
          const selectedUser = users.find(user => user.id === formData.assignedTo);
          if (selectedUser) {
            updateData.assignedToName = selectedUser.displayName || selectedUser.email;
          }
        } else {
          // If no assignedTo, clear both fields
          updateData.assignedTo = null;
          updateData.assignedToName = null;
        }
        if (formData.dueDate) {
          updateData.dueDate = new Date(formData.dueDate);
        } else {
          updateData.dueDate = null;
        }
        if (formData.estimatedHours && !isNaN(Number(formData.estimatedHours))) {
          updateData.estimatedHours = Number(formData.estimatedHours);
        } else {
          updateData.estimatedHours = null;
        }
        if (formData.actualHours && !isNaN(Number(formData.actualHours))) {
          updateData.actualHours = Number(formData.actualHours);
        } else {
          updateData.actualHours = null;
        }
        
        // Handle completedDate based on status
        if (formData.status === 'completed') {
          updateData.completedDate = new Date();
        } else {
          updateData.completedDate = null;
        }

        await updateTask(editingTask.id, updateData);
        toast.success(t('tasks.messages.updateSuccess') || 'Feladat sikeresen frissítve!');
      } else {
        // Create new task - prepare data object
        const createData: any = {
          title: formData.title.trim(),
          description: formData.description.trim(),
          priority: formData.priority,
          status: formData.status,
          tags: formData.tags,
          projectId: project.id,
          customerId: project.customerId,
          documents: []
        };

        // Only add fields if they have values (avoid undefined)
        if (formData.assignedTo) {
          createData.assignedTo = formData.assignedTo;
          // Find the user name for assignedToName
          const selectedUser = users.find(user => user.id === formData.assignedTo);
          if (selectedUser) {
            createData.assignedToName = selectedUser.displayName || selectedUser.email;
          }
        }
        if (formData.dueDate) {
          createData.dueDate = new Date(formData.dueDate);
        }
        if (formData.estimatedHours && !isNaN(Number(formData.estimatedHours))) {
          createData.estimatedHours = Number(formData.estimatedHours);
        }
        
        // Handle completedDate based on status
        if (formData.status === 'completed') {
          createData.completedDate = new Date();
        }

        await createTask(createData);
        toast.success(t('tasks.messages.createSuccess') || 'Feladat sikeresen létrehozva!');
      }
      
      handleCloseTaskForm();
    } catch (error) {
      console.error('Error saving task:', error);
      const errorMessage = editingTask 
        ? (t('tasks.messages.updateError') || 'Hiba történt a feladat frissítése során')
        : (t('tasks.messages.createError') || 'Hiba történt a feladat létrehozása során');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal bezárása
  const handleCloseTaskForm = () => {
    setIsTaskFormOpen(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      assignedTo: '',
      dueDate: '',
      estimatedHours: '',
      actualHours: '',
      tags: []
    });
  };

  const handleCloseTaskView = () => {
    setViewingTask(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${project.name} - ${t('projects.sections.tasks') || 'Feladatok'}`}
        size="5xl"
        zIndex={55}
      >
        <div className="p-4">
          {/* Conditional rendering: task list OR task form */}
          {!isTaskFormOpen ? (
            <>
              {/* Header with New Task button */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <BriefcaseIcon className="h-5 w-5 text-primary-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {projectTasks.length} {t('tasks.title') || 'feladat'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handleExportComments}
                    variant="secondary"
                    size="sm"
                    disabled={projectTasks.length === 0}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Kommentek exportálása
                  </Button>
                  <Button
                    onClick={handleCreateTask}
                    variant="primary"
                    size="sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t('tasks.actions.newTask') || 'Új feladat'}
                  </Button>
                </div>
              </div>

          {/* Tasks list */}
          {projectTasks.length > 0 ? (
            <div className="space-y-3">
              {projectTasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  {/* Task header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {task.taskNumber || 'N/A'}
                        </span>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {task.title}
                        </h4>
                      </div>
                      {task.description && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => handleViewTask(task)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        title={t('tasks.actions.viewTask') || 'Megtekintés'}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditTask(task)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title={t('tasks.actions.editTask') || 'Szerkesztés'}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task)}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title={t('tasks.actions.deleteTask') || 'Törlés'}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Task info grid with editable fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                        {/* Status - button group */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {t('tasks.fields.status') || 'Státusz'}
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {(['todo', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
                          <button
                            key={status}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task, status);
                            }}
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                              task.status === status
                                ? (() => {
                                    const isDark = document.documentElement.classList.contains('dark');
                                    switch (status) {
                                      case 'todo': return isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-800';
                                      case 'in_progress': return isDark ? 'bg-blue-600 text-blue-100' : 'bg-blue-200 text-blue-800';
                                      case 'completed': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                      case 'cancelled': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                      default: return isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-800';
                                    }
                                  })()
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {getStatusIcon(status)}
                            <span className="ml-1">
                              {status === 'todo' && (t('tasks.status.todo') || 'Tennivaló')}
                              {status === 'in_progress' && (t('tasks.status.in_progress') || 'Folyamatban')}
                              {status === 'completed' && (t('tasks.status.completed') || 'Befejezett')}
                              {status === 'cancelled' && (t('tasks.status.cancelled') || 'Megszakított')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                                        {/* Priority - button group */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {t('tasks.fields.priority') || 'Prioritás'}
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {(['low', 'medium', 'high', 'urgent'] as const).map((priority) => (
                          <button
                            key={priority}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePriorityChange(task, priority);
                            }}
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                              task.priority === priority
                                ? (() => {
                                    const isDark = document.documentElement.classList.contains('dark');
                                    switch (priority) {
                                      case 'low': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                      case 'medium': return isDark ? 'bg-yellow-600 text-yellow-100' : 'bg-yellow-200 text-yellow-800';
                                      case 'high': return isDark ? 'bg-orange-600 text-orange-100' : 'bg-orange-200 text-orange-800';
                                      case 'urgent': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                      default: return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                    }
                                  })()
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {getPriorityIcon(priority)}
                            <span className="ml-1">
                              {priority === 'low' && (t('tasks.priority.low') || 'Alacsony')}
                              {priority === 'medium' && (t('tasks.priority.medium') || 'Közepes')}
                              {priority === 'high' && (t('tasks.priority.high') || 'Magas')}
                              {priority === 'urgent' && (t('tasks.priority.urgent') || 'Sürgős')}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Assigned to */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        <UserIcon className="h-3 w-3 inline mr-1" />
                        {t('tasks.fields.assignedTo') || 'Felelős'}
                      </label>
                      <div className="text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 rounded px-2 py-1">
                        {task.assignedToName || '-'}
                      </div>
                    </div>

                    {/* Due date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        <CalendarIcon className="h-3 w-3 inline mr-1" />
                        {t('tasks.fields.dueDate') || 'Határidő'}
                      </label>
                      <div className="text-xs text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 rounded px-2 py-1">
                        {task.dueDate ? new Date(task.dueDate).toLocaleString('hu-HU', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar if needed */}
                  {task.estimatedHours && task.actualHours && (
                    <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>{t('tasks.fields.progress') || 'Haladás'}</span>
                        <span>{task.actualHours || 0} / {task.estimatedHours || 0} óra</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                        <div 
                          className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${Math.min(100, ((task.actualHours || 0) / (task.estimatedHours || 1)) * 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {task.tags && task.tags.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex flex-wrap gap-1">
                        {task.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BriefcaseIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('tasks.messages.noTasks') || 'Nincsenek feladatok'}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('tasks.messages.noTasksInProject') || 'Ehhez a projekthez még nincsenek feladatok rendelve.'}
              </p>
              <div className="mt-4">
                <Button
                  onClick={handleCreateTask}
                  variant="primary"
                  size="sm"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('tasks.actions.createFirstTask') || 'Első feladat létrehozása'}
                </Button>
              </div>
            </div>
          )}
            </>
          ) : (
            /* Inline Task Form with CustomerForm-like sections */
            <div className="space-y-6" onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleInlineSave();
              }
            }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {editingTask ? t('tasks.editTask') : t('tasks.createTask')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {editingTask 
                      ? 'Módosítsa a feladat adatait és mentse el a változtatásokat'
                      : 'Adja meg az új feladat adatait'
                    } • Ctrl+Enter a gyors mentéshez
                  </p>
                </div>
                <Button
                  onClick={handleCloseTaskForm}
                  variant="secondary"
                  size="sm"
                >
                  {t('common.back') || 'Vissza'}
                </Button>
              </div>

              {/* Alapadatok szekció */}
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('tasks.sections.basicInfo') || 'Alapadatok'}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tasks.fields.title') || 'Cím'} *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({...formData, title: e.target.value})}
                          className={`w-full bg-white dark:bg-gray-700 border rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500
                                   ${!formData.title.trim() ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'}`}
                          placeholder={t('tasks.placeholders.taskTitle') || 'Feladat címe'}
                        />
                        {!formData.title.trim() && (
                          <p className="text-red-600 dark:text-red-400 text-xs mt-1">
                            {t('tasks.validation.titleRequired') || 'Feladat címe kötelező'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tasks.fields.priority') || 'Prioritás'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {(['low', 'medium', 'high', 'urgent'] as const).map((priority) => (
                            <button
                              key={priority}
                              type="button"
                              onClick={() => setFormData({...formData, priority})}
                              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                formData.priority === priority
                                  ? (() => {
                                      const isDark = document.documentElement.classList.contains('dark');
                                      switch (priority) {
                                        case 'low': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                        case 'medium': return isDark ? 'bg-yellow-600 text-yellow-100' : 'bg-yellow-200 text-yellow-800';
                                        case 'high': return isDark ? 'bg-orange-600 text-orange-100' : 'bg-orange-200 text-orange-800';
                                        case 'urgent': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                        default: return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                      }
                                    })()
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                              }`}
                            >
                              {getPriorityIcon(priority)}
                              <span className="ml-2">
                                {priority === 'low' && (t('tasks.priority.low') || 'Alacsony')}
                                {priority === 'medium' && (t('tasks.priority.medium') || 'Közepes')}
                                {priority === 'high' && (t('tasks.priority.high') || 'Magas')}
                                {priority === 'urgent' && (t('tasks.priority.urgent') || 'Sürgős')}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tasks.fields.status') || 'Státusz'}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {(['todo', 'in_progress', 'completed', 'cancelled'] as const).map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => setFormData({...formData, status})}
                              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                formData.status === status
                                  ? (() => {
                                      const isDark = document.documentElement.classList.contains('dark');
                                      switch (status) {
                                        case 'todo': return isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-800';
                                        case 'in_progress': return isDark ? 'bg-blue-600 text-blue-100' : 'bg-blue-200 text-blue-800';
                                        case 'completed': return isDark ? 'bg-green-600 text-green-100' : 'bg-green-200 text-green-800';
                                        case 'cancelled': return isDark ? 'bg-red-600 text-red-100' : 'bg-red-200 text-red-800';
                                        default: return isDark ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-800';
                                      }
                                    })()
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                              }`}
                            >
                              {getStatusIcon(status)}
                              <span className="ml-2">
                                {status === 'todo' && (t('tasks.status.todo') || 'Teendő')}
                                {status === 'in_progress' && (t('tasks.status.in_progress') || 'Folyamatban')}
                                {status === 'completed' && (t('tasks.status.completed') || 'Kész')}
                                {status === 'cancelled' && (t('tasks.status.cancelled') || 'Megszakítva')}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tasks.fields.dueDate') || 'Határidő'}
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.dueDate}
                          onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                          className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                   focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          style={{ 
                            colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Leírás szekció */}
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <DocumentTextIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('tasks.sections.description') || 'Leírás'}</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('tasks.fields.description') || 'Leírás'}
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={4}
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder={t('tasks.placeholders.taskDescription') || 'Feladat leírása'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Hozzárendelés szekció */}
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('tasks.sections.assignment') || 'Hozzárendelés'}</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('tasks.fields.assignedTo') || 'Felelős'}
                      </label>
                      <SearchableSelect
                        options={userOptions}
                        value={(() => {
                          console.log('🔄 ProjectTasksModal SearchableSelect - current value:', formData.assignedTo);
                          console.log('🔄 ProjectTasksModal SearchableSelect - user options:', userOptions);
                          return formData.assignedTo;
                        })()}
                        onChange={handleUserSelect}
                        placeholder={t('tasks.placeholders.selectAssignee') || 'Válasszon felelőst'}
                        searchPlaceholder={t('tasks.placeholders.searchAssignee') || 'Keresés...'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Időbecslés szekció */}
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('tasks.sections.timeEstimate') || 'Időbecslés'}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('tasks.fields.estimatedHours') || 'Becsült órák'}
                        </label>
                        <input
                          type="number"
                          value={formData.estimatedHours}
                          onChange={(e) => setFormData({...formData, estimatedHours: e.target.value})}
                          className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                   focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          placeholder="0"
                          min="0"
                          step="0.5"
                        />
                      </div>
                      
                      {editingTask && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t('tasks.fields.actualHours') || 'Tényleges órák'}
                          </label>
                          <input
                            type="number"
                            value={formData.actualHours}
                            onChange={(e) => setFormData({...formData, actualHours: e.target.value})}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200
                                     focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            placeholder="0"
                            min="0"
                            step="0.5"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Címkék szekció */}
              <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex">
                  <div className="p-4 bg-gradient-to-br from-cyan-100 to-blue-100 dark:from-cyan-900/20 dark:to-blue-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                    <TagIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('tasks.sections.tags') || 'Címkék'}</h3>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
                                setFormData({
                                  ...formData,
                                  tags: [...formData.tags, tagInput.trim()]
                                });
                                setTagInput('');
                              }
                            }
                          }}
                          className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                   placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          placeholder={t('tasks.placeholders.addTag') || 'Címke hozzáadása'}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
                              setFormData({
                                ...formData,
                                tags: [...formData.tags, tagInput.trim()]
                              });
                              setTagInput('');
                            }
                          }}
                          className="px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    tags: formData.tags.filter((_, i) => i !== index)
                                  });
                                }}
                                className="ml-1 h-3 w-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mentés gombsor */}
              <div className="flex items-center justify-between pt-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t('common.shortcuts.ctrlEnter') || 'Ctrl+Enter a mentéshez'}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCloseTaskForm}
                    variant="outline"
                    size="sm"
                    disabled={isSubmitting}
                  >
                    {t('common.cancel') || 'Mégse'}
                  </Button>
                  <Button
                    onClick={handleInlineSave}
                    variant="primary"
                    size="sm"
                    disabled={isSubmitting || !formData.title.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('common.saving') || 'Mentés...'}
                      </>
                    ) : (
                      editingTask ? (t('common.save') || 'Mentés') : (t('common.create') || 'Létrehozás')
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Task view modal */}
      {viewingTask && (
        <TaskView
          task={viewingTask}
          isOpen={!!viewingTask}
          onClose={handleCloseTaskView}
        />
      )}
    </>
  );
};

export default ProjectTasksModal; 